import React, { useContext, memo } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { Accordion, useAccordionButton, AccordionContext, Tooltip, OverlayTrigger } from 'react-bootstrap'
import { Bell, HelpCircle, Mail } from 'lucide-react'
import { useQuery } from '@apollo/client'
import SidebarMenu from './SidebarMenu'
import CustomToggle from './CustomToggle'
import {
    LogoutIcon,
    SettingsIcon,
    CardIcon,
    ChevronLeftIcon,
    ProfileIcon,
    SubscriptionsIcon,
} from '@/components/icons/new'
import { Badge } from '@/components/ui/badge'
import { AuthContext } from '@/context/auth-context'
import { SettingsContext } from '@/context/settings-context'
import useNotificationsStore from '@/store/notificationsStore'
import { GET_CAMPAIGNS_FOR_PICKER } from '@/graphql/campaign-queries'
import { licenseIdFromLocation, supportLicense, supportMailto } from '@/utils/support-link'

const FooterToggle = ({ children, eventKey, onClick }) => {
    const { activeEventKey } = useContext(AccordionContext)
    const decoratedOnClick = useAccordionButton(eventKey, (active) => onClick({ state: !active, eventKey: eventKey }))
    const isCurrentEventKey = activeEventKey === eventKey

    return (
        <Link
            to="#"
            aria-expanded={isCurrentEventKey ? 'true' : 'false'}
            className="nav-link"
            role="button"
            onClick={(e) => {
                decoratedOnClick(isCurrentEventKey)
            }}
        >
            {children}
        </Link>
    )
}

const FooterNav = memo(({ onLogout }) => {
    const { onboarding } = useContext(AuthContext)
    const { sideMenuCollapsed } = useContext(SettingsContext)
    const location = useLocation()
    const params = useParams()
    const unreadCount = useNotificationsStore((s) => s.unreadCount)
    const { data: campaignsData } = useQuery(GET_CAMPAIGNS_FOR_PICKER)
    const pageLicenseId = licenseIdFromLocation({ pathname: location.pathname, params, search: location.search })
    const supportHref = supportMailto(supportLicense(pageLicenseId, campaignsData?.campaigns ?? []))

    const isDisabled = onboarding === true

    return (
        <Accordion as="ul" className="navbar-nav footer-menu">
            <Accordion.Item
                as="li"
                eventKey="sidebar-settings"
                bsPrefix={`nav-item ${location.pathname === '/profile' || location.pathname === '/billing' || location.pathname === '/subscriptions' ? 'active' : ''}`}
                style={isDisabled ? { pointerEvents: 'none', opacity: 0.5 } : {}}
            >
                <FooterToggle eventKey="sidebar-settings" onClick={(activeKey) => {}}>
                    <OverlayTrigger placement="right" overlay={<Tooltip>Settings</Tooltip>}>
                        <i className="icon text-gray-200">
                            <SettingsIcon />
                        </i>
                    </OverlayTrigger>
                    <span className="item-name">Settings</span>
                    {sideMenuCollapsed !== 'true' && (
                        <i className="right-icon text-gray-200">
                            <ChevronLeftIcon />
                        </i>
                    )}
                </FooterToggle>
                <Accordion.Collapse eventKey="sidebar-settings">
                    <ul className="sub-nav">
                        <SidebarMenu isTag="false" pathname="/profile" title="Admin Profile" miniIcon={<ProfileIcon />}>
                            <i className="icon text-gray-200">
                                <ProfileIcon />
                            </i>
                        </SidebarMenu>
                        <SidebarMenu isTag="false" pathname="/billing" title="Payment Billing" miniIcon={<CardIcon />}>
                            <i className="icon text-gray-200">
                                <CardIcon />
                            </i>
                        </SidebarMenu>
                        <SidebarMenu
                            isTag="false"
                            pathname="/subscriptions"
                            title="Subscriptions"
                            miniIcon={<SubscriptionsIcon />}
                        >
                            <i className="icon text-gray-200">
                                <SubscriptionsIcon />
                            </i>
                        </SidebarMenu>
                    </ul>
                </Accordion.Collapse>
            </Accordion.Item>

            <li className={`nav-item ${location.pathname.startsWith('/notifications') ? 'active' : ''}`}>
                <Link to="/notifications" className="nav-link">
                    <OverlayTrigger placement="right" overlay={<Tooltip>Notifications</Tooltip>}>
                        <i className="icon text-gray-200 tw:relative">
                            <Bell size={20} />
                            {unreadCount > 0 && (
                                <Badge
                                    className="notif-badge-mini tw:absolute tw:-top-1.5 tw:-right-1.5 tw:min-w-4 tw:h-4 tw:px-1 tw:flex tw:items-center tw:justify-center tw:rounded-full tw:text-[9px] tw:font-semibold tw:leading-none tw:text-white tw:border-0"
                                    style={{ backgroundColor: 'var(--notif-unread, #2450bf)' }}
                                >
                                    {unreadCount > 99 ? '99+' : unreadCount}
                                </Badge>
                            )}
                        </i>
                    </OverlayTrigger>
                    <span className="item-name tw:!flex-none">Notifications</span>
                    {unreadCount > 0 && (
                        <Badge
                            className="notif-badge-full tw:ml-2 tw:min-w-5 tw:h-5 tw:px-1 tw:flex tw:items-center tw:justify-center tw:rounded-full tw:text-[11px] tw:font-semibold tw:leading-none tw:text-white tw:border-0"
                            style={{ backgroundColor: 'var(--notif-unread, #2450bf)' }}
                        >
                            {unreadCount}
                        </Badge>
                    )}
                </Link>
            </li>

            <li className="nav-item">
                <a href="https://turbamedia.io/flow" target="_blank" rel="noopener noreferrer" className="nav-link">
                    <OverlayTrigger placement="right" overlay={<Tooltip>User Manual</Tooltip>}>
                        <i className="icon text-gray-200">
                            <HelpCircle size={20} />
                        </i>
                    </OverlayTrigger>
                    <span className="item-name tw:!flex-none">User Manual</span>
                </a>
            </li>

            <li className="nav-item">
                <a href={supportHref} className="nav-link">
                    <OverlayTrigger placement="right" overlay={<Tooltip>Contact Support</Tooltip>}>
                        <i className="icon text-gray-200">
                            <Mail size={20} />
                        </i>
                    </OverlayTrigger>
                    <span className="item-name tw:!flex-none">Contact Support</span>
                </a>
            </li>

            <Accordion.Item as="li" eventKey="sidebar-logout">
                <CustomToggle eventKey="sidebar-logout">
                    <li className="nav-item mb-0">
                        <a href="#" onClick={() => onLogout(false)} className="nav-link">
                            <OverlayTrigger placement="right" overlay={<Tooltip>Sign out</Tooltip>}>
                                <i className="icon text-gray-200">
                                    <LogoutIcon />
                                </i>
                            </OverlayTrigger>
                            <span className="item-name">Sign out</span>
                        </a>
                    </li>
                </CustomToggle>
            </Accordion.Item>
        </Accordion>
    )
})

FooterNav.displayName = 'FooterNav'
export default FooterNav
